/**
 * ProofStats — three-up activity strip on the Home page.
 *
 * Renders only when the user has at least one proof. Shows the
 * proof count, the most recent proof's date, and the count of
 * proofs in the last seven days. Per [docs/DASHBOARD-HOME-EXPLORE-
 * IMPLEMENTATION.md §2.3].
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Activity, Calendar, Clock } from 'lucide-react';

import type { Proof } from '@/api/proofs';
import { arrive } from '@/lib/motion';
import { cn } from '@/utils/cn';

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function ProofStats({ proofs }: { proofs: Proof[] }) {
  const { t, i18n } = useTranslation('dashboard');

  const { total, lastDate, thisWeek } = useMemo(() => {
    const total = proofs.length;
    const lastTs = proofs[0]?.timestamp;
    const lastDate = lastTs ? new Date(lastTs) : null;
    const cutoff = Date.now() - ONE_WEEK_MS;
    const thisWeek = proofs.filter(
      (p) => new Date(p.timestamp).getTime() >= cutoff,
    ).length;
    return { total, lastDate, thisWeek };
  }, [proofs]);

  const fmt = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
  });

  return (
    <motion.section
      aria-label={t('stats.aria_label')}
      className="mt-10 grid gap-3 sm:grid-cols-3"
      variants={arrive}
      initial="hidden"
      animate="visible"
    >
      <Stat
        icon={<Activity className="h-4 w-4" aria-hidden />}
        label={t('stats.total')}
        value={String(total)}
      />
      <Stat
        icon={<Calendar className="h-4 w-4" aria-hidden />}
        label={t('stats.last')}
        value={lastDate ? fmt.format(lastDate) : '—'}
      />
      {thisWeek > 0 ? (
        <Stat
          icon={<Clock className="h-4 w-4" aria-hidden />}
          label={t('stats.this_week')}
          value={t('stats.this_week_value', { count: thisWeek })}
        />
      ) : (
        <Stat
          icon={<Clock className="h-4 w-4" aria-hidden />}
          label={t('stats.this_week')}
          value="—"
          muted
        />
      )}
    </motion.section>
  );
}

function Stat({
  icon,
  label,
  value,
  muted,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle',
        muted && 'opacity-60',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-spark-soft text-spark">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-ink-muted">
          {label}
        </p>
        <p className="truncate text-sm font-semibold text-ink">{value}</p>
      </div>
    </div>
  );
}
